"use client";
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Menu, X, ShoppingBasket, ExternalLink } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useTranslation } from '@/lib/i18n/useTranslation';
import LanguageToggle from '@/components/LanguageToggle';

const GOLD = '#D48937';

const Navbar = ({ categories, selectedCategory, onCategorySelect } = {}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const pathname = usePathname();
    const { t } = useTranslation();

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    const toggleMenu = () => setIsMenuOpen((prev) => !prev);
    const closeMenu = () => setIsMenuOpen(false);

    const labels = {
        brand: t('nav.brand'),
        home: t('nav.home'),
        about: t('nav.about'),
        contact: t('nav.contact'),
        categories: t('nav.categories'),
    };

    const hasCategories = Array.isArray(categories) && categories.length > 0;

    const rowBaseCls =
        'flex w-full items-center gap-3 px-5 py-2.5 text-left text-[11px] uppercase tracking-[0.3em] transition-colors hover:bg-neutral-100 hover:text-neutral-900';

    const accentDot = (active) => (
        <span
            aria-hidden
            className={`h-1 w-1 shrink-0 rounded-full ${active ? '' : 'opacity-0'}`}
            style={{ backgroundColor: GOLD }}
        />
    );

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 text-black bg-transparent backdrop-blur-sm [transform:translateZ(0)]">
            <div className="flex justify-between items-center mx-4 md:mx-12 py-3">
                {isMobile ? (
                    <>
                        <Link href="/?category=objekte" className="text-left text-xl font-light hover:opacity-70 transition-opacity">
                            {labels.brand}
                        </Link>
                        <div className="flex items-center gap-3">
                            <LanguageToggle />
                            <div className="relative">
                                <button
                                    onClick={toggleMenu}
                                    className="-mr-2 p-2 text-neutral-900"
                                    aria-label="menu"
                                    aria-expanded={isMenuOpen}
                                >
                                    {isMenuOpen ? (
                                        <X className="h-7 w-7" strokeWidth={1.25} />
                                    ) : (
                                        <Menu className="h-7 w-7" strokeWidth={1.25} />
                                    )}
                                </button>

                                {isMenuOpen && (
                                    <>
                                        <button
                                            type="button"
                                            aria-hidden
                                            tabIndex={-1}
                                            onClick={closeMenu}
                                            className="fixed inset-0 z-40 cursor-default"
                                        />
                                        <div className="absolute right-0 z-50 mt-3 w-60 max-h-[calc(100vh-5rem)] overflow-y-auto border border-neutral-200 bg-white font-pp-neue shadow-[0_12px_40px_-12px_rgba(0,0,0,0.28)]">
                                            {hasCategories && (
                                                <div className="border-b border-neutral-200 py-2">
                                                    <p className="px-5 pb-1 pt-2 text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                                                        {labels.categories}
                                                    </p>
                                                    {categories.map((category) => {
                                                        if (category.externalUrl) {
                                                            return (
                                                                <a
                                                                    key={category.id}
                                                                    href={category.externalUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={closeMenu}
                                                                    className={`${rowBaseCls} text-neutral-500`}
                                                                >
                                                                    {accentDot(false)}
                                                                    <span className="flex-1">{category.name}</span>
                                                                    <ExternalLink className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                                                                </a>
                                                            );
                                                        }

                                                        const active = selectedCategory === category.id;
                                                        return (
                                                            <button
                                                                key={category.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    onCategorySelect?.(category.id);
                                                                    closeMenu();
                                                                }}
                                                                className={`${rowBaseCls} ${active ? 'text-neutral-900' : 'text-neutral-500'}`}
                                                            >
                                                                {accentDot(active)}
                                                                <span className="flex-1">{category.name}</span>
                                                                {category.id === 'shop' && (
                                                                    <ShoppingBasket className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            <div className="py-2">
                                                <Link
                                                    href="/"
                                                    onClick={closeMenu}
                                                    className={`${rowBaseCls} ${pathname === '/' ? 'text-neutral-900' : 'text-neutral-500'}`}
                                                >
                                                    {accentDot(pathname === '/')}
                                                    <span className="flex-1">{labels.home}</span>
                                                </Link>
                                                <Link
                                                    href="/about"
                                                    onClick={closeMenu}
                                                    className={`${rowBaseCls} ${pathname === '/about' ? 'text-neutral-900' : 'text-neutral-500'}`}
                                                >
                                                    {accentDot(pathname === '/about')}
                                                    <span className="flex-1">{labels.about}</span>
                                                </Link>
                                                <Link
                                                    href="/contact"
                                                    onClick={closeMenu}
                                                    className={`${rowBaseCls} ${pathname === '/contact' ? 'text-neutral-900' : 'text-neutral-500'}`}
                                                >
                                                    {accentDot(pathname === '/contact')}
                                                    <span className="flex-1">{labels.contact}</span>
                                                </Link>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex-1">
                            <Link href="/?category=objekte" className="text-left text-xl font-light hover:opacity-70 transition-opacity">
                                {labels.brand}
                            </Link>
                        </div>
                        <div className="flex-1 flex justify-center">
                            <Link
                                href="/about"
                                className={`font-light hover:opacity-70 transition-opacity relative group ${pathname === '/about' ? 'font-bold' : ''}`}
                            >
                                {labels.about}
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-black transition-all group-hover:w-full" />
                            </Link>
                        </div>
                        <div className="flex-1 flex justify-end items-center gap-6">
                            <Link href="/contact" className={`font-light relative group ${pathname === '/contact' ? 'font-bold' : ''}`}>
                                <span className="relative inline-block">
                                    {labels.contact}
                                    <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-black origin-left transform scale-x-100 animate-[pulse_1.5s_ease-in-out_infinite]" />
                                </span>
                            </Link>
                            <LanguageToggle />
                        </div>
                    </>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
